import React, { useState, useEffect } from 'react';
import { Users, Clock, FileText, Gift, Cake, CalendarDays, TrendingUp, AlertTriangle, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, getUpcomingBirthdays, getUpcomingAnniversaries, getYearsOfService } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { generateAppraisalExcel, generateAppraisalPDF, generateDashboardReportPDF, generateDashboardReportExcel } from '../../../lib/export';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

const COLORS = ['#0d9488', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444'];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData, invitesData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray(),
        db.invites.toArray()
      ]);
      setUsers(usersData);
      setAppraisals(appraisalsData);
      setInvites(invitesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeUsers = users.filter(u => u.status !== 'deleted');
  const employees = activeUsers.filter(u => u.role === 'employee');
  const supervisors = activeUsers.filter(u => u.role === 'supervisor');
  const pendingAppraisals = appraisals.filter(a => a.status === 'employee_completed');
  const newHires = users.filter(u => {
    const hireDate = new Date(u.profile?.hireDate);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return hireDate > thirtyDaysAgo && u.role !== 'admin';
  });

  const upcomingBirthdays = getUpcomingBirthdays(activeUsers.filter(u => u.role !== 'admin'), 7);
  const upcomingAnniversaries = getUpcomingAnniversaries(activeUsers.filter(u => u.role !== 'admin'), 30);

  const getNextOccurrence = (dateValue) => {
    if (!dateValue) return Infinity;
    const base = typeof dateValue === 'number' ? new Date(dateValue) : new Date(dateValue);
    const today = new Date();
    const next = new Date(base);
    next.setFullYear(today.getFullYear());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    return next.getTime();
  };

  const sortedBirthdays = [...upcomingBirthdays].sort((a, b) => getNextOccurrence(a.profile?.dateOfBirth) - getNextOccurrence(b.profile?.dateOfBirth));
  const sortedAnniversaries = [...upcomingAnniversaries].sort((a, b) => getNextOccurrence(a.profile?.hireDate) - getNextOccurrence(b.profile?.hireDate));

  const statusData = [
    { name: 'Finalized', value: appraisals.filter(a => a.status === 'finalized').length },
    { name: 'Pending Review', value: appraisals.filter(a => a.status === 'employee_completed').length },
    { name: 'In Progress', value: appraisals.filter(a => a.status === 'ready_to_evaluate').length },
    { name: 'Draft', value: appraisals.filter(a => a.status === 'draft').length },
  ];

  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];
  const departmentData = departments.map(dept => ({
    name: dept,
    employees: users.filter(u => u.profile?.department === dept && u.role !== 'admin').length,
    appraisals: appraisals.filter(a => {
      const user = users.find(u => u.id === a.userId);
      return user?.profile?.department === dept;
    }).length
  }));

  const getUserById = (id) => users.find(u => u.id === id);

  const buildSummary = () => ({
    employees: employees.length,
    supervisors: supervisors.length,
    pending: pendingAppraisals.length,
    newHires: newHires.length,
    activeInvites: invites.filter(i => i.status === 'active').length,
    statusData,
    departmentData
  });

  const handleExportPDF = () => {
    const summary = buildSummary();
    try {
      const fileName = generateDashboardReportPDF(summary, appraisals, users);
      toast.success(`PDF exported: ${fileName}`);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
    setShowReportModal(false);
  };

  const handleExportExcel = () => {
    const summary = buildSummary();
    try {
      const fileName = generateDashboardReportExcel(summary, appraisals, users);
      toast.success(`Excel exported: ${fileName}`);
    } catch (error) {
      toast.error('Failed to generate Excel');
    }
    setShowReportModal(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of your organization's performance</p>
        </div>
        <Button onClick={() => setShowReportModal(true)} className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white">
          <FileText className="w-4 h-4 mr-2" /> Generate Report
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{employees.length}</p>
                <p className="text-xs text-emerald-600 mt-1">+{newHires.length} this month</p>
              </div>
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Reviews</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{pendingAppraisals.length}</p>
                <p className="text-xs text-amber-600 mt-1">Action required</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Supervisors</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{supervisors.length}</p>
                <p className="text-xs text-gray-400 mt-1">Active managers</p>
              </div>
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Invites</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{invites.filter(i => i.status === 'active').length}</p>
                <p className="text-xs text-gray-400 mt-1">Unused invites</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Gift className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="employees" className="data-[state=active]:bg-white">My Employees</TabsTrigger>
          <TabsTrigger value="approvals" className="data-[state=active]:bg-white">My Approvals</TabsTrigger>
          <TabsTrigger value="newhire" className="data-[state=active]:bg-white">New Hire</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader><CardTitle className="text-lg">Department Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={departmentData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="employees" name="Employees" fill="#0d9488" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="appraisals" name="Appraisals" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg">Status Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                        {statusData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {statusData.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                      <span className="text-gray-600">{item.name} ({item.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Pending Approvals</CardTitle>
                  <Badge variant="secondary">{pendingAppraisals.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingAppraisals.length > 0 ? pendingAppraisals.slice(0, 3).map(appraisal => {
                  const employee = getUserById(appraisal.userId);
                  return (
                    <div key={appraisal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <div>
                        <p className="font-medium text-sm text-gray-900">{employee?.profile?.firstName} {employee?.profile?.lastName}</p>
                        <p className="text-xs text-gray-500">{appraisal.title}</p>
                      </div>
                      <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
                    </div>
                  );
                }) : <p className="text-sm text-gray-500 text-center py-4">No pending approvals</p>}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2"><Cake className="w-5 h-5 text-pink-500" />Upcoming Birthdays</CardTitle>
                  <Badge variant="secondary">{upcomingBirthdays.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingBirthdays.length > 0 ? sortedBirthdays.slice(0, 3).map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {user.profile?.firstName?.[0]}{user.profile?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{user.profile?.firstName} {user.profile?.lastName}</p>
                        <p className="text-xs text-gray-500">{user.profile?.department}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">{formatDate(user.profile?.dateOfBirth, 'MMM d')}</p>
                  </div>
                )) : <p className="text-sm text-gray-500 text-center py-4">No birthdays this week</p>}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2"><CalendarDays className="w-5 h-5 text-violet-500" />Work Anniversaries</CardTitle>
                  <Badge variant="secondary">{upcomingAnniversaries.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingAnniversaries.length > 0 ? sortedAnniversaries.slice(0, 3).map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {user.profile?.firstName?.[0]}{user.profile?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{user.profile?.firstName} {user.profile?.lastName}</p>
                        <p className="text-xs text-gray-500">{getYearsOfService(user.profile?.hireDate)} years</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">{formatDate(user.profile?.hireDate, 'MMM d')}</p>
                  </div>
                )) : <p className="text-sm text-gray-500 text-center py-4">No anniversaries this month</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>All Employees</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Department</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Position</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role !== 'admin').map(user => (
                      <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {user.profile?.firstName?.[0]}{user.profile?.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{user.profile?.firstName} {user.profile?.lastName}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{user.profile?.department}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{user.profile?.position}</td>
                        <td className="py-3 px-4">
                          <Badge className={user.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>{user.status}</Badge>
                        </td>
                        <td className="py-3 px-4 text-right"><Button variant="ghost" size="sm">View</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Pending Approvals</CardTitle></CardHeader>
            <CardContent>
              {pendingAppraisals.length > 0 ? (
                <div className="space-y-4">
                  {pendingAppraisals.map(appraisal => {
                    const employee = getUserById(appraisal.userId);
                    return (
                      <div key={appraisal.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:border-gray-200 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-medium">
                            {employee?.profile?.firstName?.[0]}{employee?.profile?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{employee?.profile?.firstName} {employee?.profile?.lastName}</p>
                            <p className="text-sm text-gray-500">{appraisal.title}</p>
                            <p className="text-xs text-gray-400 mt-1">Due: {formatDate(appraisal.dueDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">Review</Button>
                          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white">Approve</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-gray-400" /></div>
                  <p className="text-gray-500">No pending approvals</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="newhire" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>New Hires (Last 30 Days)</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {newHires.length > 0 ? (
                <div className="space-y-4">
                  {newHires.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-medium">
                          {user.profile?.firstName?.[0]}{user.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.profile?.firstName} {user.profile?.lastName}</p>
                          <p className="text-sm text-gray-500">{user.profile?.position} • {user.profile?.department}</p>
                          <p className="text-xs text-gray-400 mt-1">Started: {formatDate(user.profile?.hireDate)}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">Assign Appraisal</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Users className="w-8 h-8 text-gray-400" /></div>
                  <p className="text-gray-500">No new hires in the last 30 days</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Export Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Report</DialogTitle>
            <DialogDescription>Choose the format for your report</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button onClick={handleExportPDF} variant="outline" className="w-full justify-start h-16">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-rose-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Export as PDF</p>
                  <p className="text-sm text-gray-500">Single appraisal detailed report</p>
                </div>
              </div>
            </Button>
            <Button onClick={handleExportExcel} variant="outline" className="w-full justify-start h-16">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Export as Excel</p>
                  <p className="text-sm text-gray-500">All appraisals data spreadsheet</p>
                </div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
